import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { WarningTriangle, ArrowRight, Xmark } from "iconoir-react";
import { api } from "@/lib/api";
import { useOrg } from "@/lib/orgContext";

// Dismissed warnings are remembered per (org, resource, period) only for
// the current browser session — they re-appear on next visit so users keep
// seeing the upgrade nudge.
const dismissedKey = (orgId, resource) => `aivos_dismiss_${orgId}_${resource}`;

export default function QuotaBanner() {
  const { activeOrgId } = useOrg();
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0); // forces re-eval after dismiss

  useEffect(() => {
    if (!activeOrgId) {
      setData(null);
      return;
    }
    let cancelled = false;
    api
      .get(`/v1/organizations/${activeOrgId}/billing`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [activeOrgId]);

  if (!activeOrgId || !data?.warnings?.length) return null;

  const visible = data.warnings.filter(
    (w) => !sessionStorage.getItem(dismissedKey(activeOrgId, w.resource)),
  );
  if (visible.length === 0) return null;

  // Show the highest-severity warning first; group the rest as a count
  const sorted = [...visible].sort((a, b) => {
    if (a.severity !== b.severity) return a.severity === "exceeded" ? -1 : 1;
    return b.pct - a.pct;
  });
  const top = sorted[0];
  const isExceeded = top.severity === "exceeded";
  const others = sorted.length - 1;

  const dismiss = () => {
    sessionStorage.setItem(dismissedKey(activeOrgId, top.resource), "1");
    setTick((t) => t + 1);
  };
  // Silence unused-var lint; tick is intentionally used to force re-render
  void tick;

  return (
    <div
      data-testid="quota-banner"
      data-severity={top.severity}
      className={`border ${isExceeded ? "border-intl bg-intl/5" : "border-amber-400 bg-amber-50"} px-5 py-4 mb-8 flex items-start gap-4`}
    >
      <div className={`mt-0.5 ${isExceeded ? "text-intl" : "text-amber-700"}`}>
        <WarningTriangle width={20} height={20} strokeWidth={1.6} />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-mono text-[10px] uppercase tracking-widest ${isExceeded ? "text-intl" : "text-amber-700"}`}>
          {isExceeded ? "Plan limit reached" : "Approaching plan limit"}
        </div>
        <div className="mt-1 text-sm font-app" data-testid="quota-banner-message">
          You’ve used <strong>{top.used}/{top.allowed}</strong> ({top.pct}%) of your{" "}
          <strong>{top.label}</strong> quota on the <strong className="capitalize">{data.plan_key}</strong> plan.
          {isExceeded
            ? " New requests are being blocked until upgrade or monthly reset."
            : " Upgrade to avoid hitting the cap."}
          {others > 0 && (
            <span className="text-muted-foreground"> +{others} other limit{others > 1 ? "s" : ""} also nearing.</span>
          )}
        </div>
      </div>
      <Link
        to="/billing"
        data-testid="quota-banner-upgrade"
        className={`flex items-center gap-1 px-3 py-1.5 text-xs font-app uppercase tracking-widest ${isExceeded ? "bg-intl text-white" : "bg-obsidian text-bone"} hover:opacity-90`}
      >
        Upgrade <ArrowRight width={12} height={12} />
      </Link>
      <button
        onClick={dismiss}
        data-testid="quota-banner-dismiss"
        className="text-muted-foreground hover:text-foreground"
        title="Dismiss until next session"
      >
        <Xmark width={16} height={16} />
      </button>
    </div>
  );
}
